import { CONFIG } from "@rentiohq/shared-frontend/dist/config/app.config";
import withKycRequired from "@rentiohq/shared-frontend/dist/hoc/withKycRequired";
import { useCountDown } from "@rentiohq/shared-frontend/dist/hooks/useCountDown";
import * as paymentRequestApi from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.api";
import * as paymentRequestHooks from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.hooks";
import { EPaymentMethod } from "@rentiohq/shared-frontend/dist/types/payment.types";
import {
  EPaymentRequestFeature,
  EPaymentRequestStatus,
  IPaymentRequest,
} from "@rentiohq/shared-frontend/dist/types/paymentRequest.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { EAssets } from "@rentiohq/web-shared-next/dist/assets";
import {
  Button,
  Icon,
  MainCard,
  SpinningLoader,
  Typography,
} from "@rentiohq/web-shared-next/dist/ui-components";
import { enqueueSnackbar } from "@rentiohq/web-shared-next/dist/utils/snackbar";
import { EButtonVariants } from "@rentiohq/web-shared-next/dist/utils/types/button";
import React from "react";
import { useParams, useNavigate } from "react-router";
import { useRedirectIfPlannedCollection } from "../../useRedirectIfPlannedCollection";
import { PaymentLimitStyles } from "./PaymentLimit.styles";

const PaymentLimitBase = () => {
  const params = useParams();
  const navigate = useNavigate();

  const sx = PaymentLimitStyles();

  const paymentRequestId = params.paymentRequestId
    ? parseInt(params.paymentRequestId, 0)
    : undefined;

  const selectedPaymentMethod = params.paymentMethod;

  const { remainingCountDownInSeconds } = useCountDown(
    CONFIG.PAYMENT_LIMIT_AMOUNT_MESSAGE_DELAY,
  );

  const [isPaying, setIsPaying] = React.useState(false);

  const {
    detail: paymentRequest,
    isFetching,
    fetchError,
  } = paymentRequestHooks.useDetail({
    shouldRefetch: false,
    id: paymentRequestId,
  });

  React.useEffect(() => {
    if (
      paymentRequest &&
      paymentRequest.status === EPaymentRequestStatus.Paid
    ) {
      enqueueSnackbar({
        message: getLocalizedText("payment.error.already_paid.title"),
        variant: "error",
      });
      navigate("/payments");
    }
  }, [paymentRequest]);

  const isPlannedCollection = useRedirectIfPlannedCollection(paymentRequest);

  React.useEffect(() => {
    if (fetchError) {
      enqueueSnackbar({ variant: "error", message: fetchError.message });
    }
  }, [fetchError]);

  if (!paymentRequest) {
    return null;
  }

  const getPaymentLimitBtnTitle = () => {
    let title = getLocalizedText("payment_limit_message.button");
    if (remainingCountDownInSeconds) {
      title += ` (${remainingCountDownInSeconds})`;
    }

    return title;
  };

  const callbackUrl = paymentRequestId
    ? `${window.origin}/payment/${paymentRequestId}/status`
    : undefined;

  const handlePressSelect = () => {
    if (!selectedPaymentMethod) {
      enqueueSnackbar({
        message: getLocalizedText(
          "payment.online.select_method.cta_info.select",
        ),
        variant: "info",
      });

      return;
    }

    if (!paymentRequestId) {
      return;
    }

    switch (selectedPaymentMethod) {
      case EPaymentMethod.BancontactOrIdeal:
      case EPaymentMethod.Bancontact:
        handlePressWebPayment({
          paymentMethod: EPaymentMethod.Bancontact,
          paymentRequest,
        });
        break;

      case EPaymentMethod.Payconiq:
        handlePressWebPayment({
          paymentMethod: EPaymentMethod.Payconiq,
          paymentRequest,
        });
        break;

      case EPaymentMethod.Ideal:
        handlePressWebPayment({
          paymentMethod: EPaymentMethod.Ideal,
          paymentRequest,
        });
        break;

      default:
        break;
    }
  };

  const handleMollieFlow = async () => {
    if (!paymentRequestId || !callbackUrl) {
      return;
    }

    const { data } = await paymentRequestApi.getMollieUrl(
      paymentRequestId,
      callbackUrl,
    );

    const { redirectUrl } = data;

    window.open(redirectUrl, "_self");
  };

  const handlePaymentWithBanqup = async (paymentMethod: EPaymentMethod) => {
    if (!paymentRequestId || !callbackUrl) {
      return;
    }

    try {
      const { data } = await paymentRequestApi.generateBanqupWebPayment(
        paymentRequestId,
        paymentMethod,
        callbackUrl,
      );

      const { redirectUrl } = data;

      if (!redirectUrl) {
        throw new Error("No redirectUrl or templateUrl found for web payment");
      }

      setTimeout(() => {
        window.open(redirectUrl, "_self");
      }, 20);
      setIsPaying(false);
    } catch (error) {
      enqueueSnackbar({
        message: error as any,
        variant: "error",
      });
      setIsPaying(false);
    }
  };

  const handlePaymentWithMangoPay = async (paymentMethod: EPaymentMethod) => {
    if (!paymentRequestId || !callbackUrl) {
      return;
    }

    const { data } = await paymentRequestApi.generateWebPayment(
      paymentRequestId,
      paymentMethod,
      callbackUrl,
    );

    const { redirectUrl } = data;

    if (!redirectUrl) {
      throw new Error("No redirectUrl or templateUrl found for web payment");
    }

    setTimeout(() => {
      window.open(redirectUrl, "_self");
    }, 20);
  };

  const handlePressWebPayment = async (params: {
    paymentMethod: EPaymentMethod;
    paymentRequest: IPaymentRequest;
  }) => {
    if (!paymentRequestId || !callbackUrl) {
      return;
    }

    const { paymentMethod, paymentRequest } = params;

    // A planned collection gets no web payment: direct debit already collects
    // it, and a second pay-in would collect it twice.
    if (isPlannedCollection) {
      return;
    }

    setIsPaying(true);

    try {
      // Legacy Mollie flow
      const isLegacyMollieFlow = false;
      if (isLegacyMollieFlow) {
        handleMollieFlow();
        return;
      }

      if (
        paymentRequest?.features.includes(
          EPaymentRequestFeature.BanqupRentDeposit,
        )
      ) {
        handlePaymentWithBanqup(paymentMethod);
        return;
      }

      handlePaymentWithMangoPay(paymentMethod);
    } catch (unknownError) {
      const error = unknownError as any;
      enqueueSnackbar({
        message: error.message,
        variant: "error",
      });
      setIsPaying(false);
    }
  };

  const isLoading = isFetching || isPaying;
  return (
    <MainCard contentSX={sx.wrapper}>
      {isLoading ? (
        <SpinningLoader />
      ) : (
        <>
          <Icon
            path={EAssets.AlertIcon}
            realColor="#ec9c3d"
            width="188px"
            height="188px"
          />
          <Typography variant="h2" mt={12.5}>
            {getLocalizedText("payment_limit_message.header")}
          </Typography>
          {selectedPaymentMethod && (
            <Typography variant="body1" my={5} sx={sx.content.info}>
              {getLocalizedText("payment_limit_message.body", {
                paymentMethod: getLocalizedText(
                  `payment_request.method.${selectedPaymentMethod}`,
                ),
              })}
            </Typography>
          )}
          <Button
            variant={EButtonVariants.Contained}
            onClick={handlePressSelect}
            disabled={remainingCountDownInSeconds > 0}
          >
            {getPaymentLimitBtnTitle()}
          </Button>
        </>
      )}
    </MainCard>
  );
};

export const PaymentLimit = withKycRequired(PaymentLimitBase);
